import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Plane } from "lucide-react";

export function AirportsSection() {
  const [rows, setRows] = useState<any[]>([]);
  const [form, setForm] = useState({ code: "", name: "" });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const { toast } = useToast();

  const load = async () => {
    const { data, error } = await supabase.from("airports").select("*");
    if (error) { toast({ title: "Error", description: error.message, variant: "destructive" }); return; }
    setRows(data || []);
  };

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    const payload = { code: form.code, name: form.name };
    if (editingId) {
      const { error } = await supabase.from("airports").update(payload).eq("id", editingId);
      if (error) { toast({ title: "Error", description: error.message, variant: "destructive" }); return; }
    } else {
      const { error } = await supabase.from("airports").insert(payload);
      if (error) { toast({ title: "Error", description: error.message, variant: "destructive" }); return; }
    }
    setForm({ code: "", name: "" });
    setEditingId(null);
    setIsDialogOpen(false);
    load();
    toast({ title: editingId ? "Actualizado" : "Creado", description: "Aeropuerto guardado correctamente." });
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    const { error } = await supabase.from("airports").delete().eq("id", deleteId);
    if (error) { toast({ title: "Error", description: error.message, variant: "destructive" }); return; }
    setDeleteId(null);
    load();
    toast({ title: "Eliminado", description: "Aeropuerto eliminado." });
  };

  const openCreate = () => { setForm({ code: "", name: "" }); setEditingId(null); setIsDialogOpen(true); };
  const openEdit = (row: any) => { setForm({ code: row.code, name: row.name }); setEditingId(row.id); setIsDialogOpen(true); };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">Aeropuertos</h2>
          <p className="text-muted-foreground text-sm mt-1">Gestiona los aeropuertos disponibles <Badge variant="secondary" className="ml-2">{rows.length}</Badge></p>
        </div>
        <Button onClick={openCreate}><Plus className="h-4 w-4 mr-1" /> Agregar</Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <Plane className="h-10 w-10 mb-3 opacity-40" />
              <p>No hay aeropuertos registrados</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Código</TableHead>
                  <TableHead>Nombre</TableHead>
                  <TableHead className="w-[100px] text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(row => (
                  <TableRow key={row.id}>
                    <TableCell><Badge variant="outline" className="font-mono">{row.code}</Badge></TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost" onClick={() => openEdit(row)}><Pencil className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" className="text-destructive" onClick={() => setDeleteId(row.id)}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingId ? "Editar Aeropuerto" : "Nuevo Aeropuerto"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Código (IATA)</Label><Input value={form.code} onChange={e => setForm({ ...form, code: e.target.value })} placeholder="ej. SJO" /></div>
            <div><Label>Nombre</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="ej. Aeropuerto Juan Santamaría" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave}>{editingId ? "Actualizar" : "Crear"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar aeropuerto?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
